import { useEffect, useMemo, useState } from "react";

import { Wallet } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { useBalance } from "@/entities/balance/api";
import { Skeleton } from "@/shared/ui/skeleton.tsx";
import { numberWithSpaces } from "@/shared/lib/utils";
import { useSessionStore } from "@/entities/session/store";
import {
  getFTCOINBalance,
  getUSDTBalance,
} from "@/entities/balance/utils/get-balance";
import { ToggleGroup, ToggleGroupItem } from "@/shared/ui/toggle-group";
import { FTCoin } from "@/shared/ui/svg/FTCoin";
import { PayoutModal } from "@/widgets/PayoutModal";
import { RandomQuoteModal } from "@/widgets/RandomQuoteModal";
import { useSwitchPayoutModal } from "@/widgets/PayoutModal/hooks/useSwitchPayoutModal";
import CurrencySwitcher from "@/shared/ui/currency-switcher";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/tooltip";

export const BalanceGroup = () => {
  const { user, updateUser } = useSessionStore();
  const { data, isFetching } = useBalance({ userId: user?._id! });
  const [currency, setCurrency] = useState<"USD" | "RUB">("USD");

  const {
    payoutModalOpen,
    quoteModalOpen,
    handlePayoutRequest,
    handleQuoteModalChange,
    handlePayoutModalChange,
  } = useSwitchPayoutModal();

  useEffect(() => {
    updateUser({
      balances: data?.data,
    });
  }, [data?.data, updateUser]);

  const [randomQuote, setRandomQuote] = useState<{
    text: string;
    author: string;
  } | null>(null);

  const [type, setType] = useState("usdt");

  const [usdt, rub] = useMemo(() => getUSDTBalance(data?.data), [data]);
  const ftcoin = useMemo(() => getFTCOINBalance(data?.data), [data]);

  return (
    <div className="relative flex w-full flex-col gap-2">
      <ToggleGroup
        type="single"
        value={type}
        className="min-h-10 rounded-lg bg-[#f1f1f3] dark:bg-[#191919]"
      >
        <ToggleGroupItem
          onClick={() => setType("usdt")}
          className="flex-1 rounded-lg data-[state=on]:bg-secondary data-[state=on]:text-onSecondary"
          value="usdt"
        >
          Баланс
        </ToggleGroupItem>
        <ToggleGroupItem
          onClick={() => setType("ftcoin")}
          className="flex-1 rounded-lg data-[state=on]:bg-secondary data-[state=on]:text-onSecondary"
          value="ftcoin"
        >
          <FTCoin />
          FT Coin
        </ToggleGroupItem>
      </ToggleGroup>
      <div className="flex gap-1 max-md:justify-between md:flex-col">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button className="bg-textColor text-white" size={"icon"}>
              <Wallet />
            </Button>

            <div className="flex flex-col items-start">
              <span className="text-sm text-textGrayColor">Баланс</span>

              {isFetching ? (
                <Skeleton className="h-4 w-full" />
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-semibold text-textColor">
                        {type === "usdt"
                          ? currency === "USD"
                            ? `$ ${numberWithSpaces(usdt)}`
                            : `₽ ${numberWithSpaces(rub)}`
                          : `FT ${numberWithSpaces(ftcoin)}`}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      className="bg-[#1C1C1C] py-2 dark:bg-[#1C1C1C]"
                    >
                      <span className="px-2 py-4 font-semibold text-white">
                        {type === "usdt"
                          ? currency === "USD"
                            ? `≈ ₽ ${numberWithSpaces(rub)}`
                            : `≈ $ ${numberWithSpaces(usdt)}`
                          : `FT ${numberWithSpaces(ftcoin)}`}
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
          {type === "usdt" && <CurrencySwitcher onSwitch={setCurrency} />}
        </div>
        {type === "usdt" ? (
          <>
            <div className="z-[10] flex max-md:w-36 [&_button]:w-full">
              <PayoutModal
                open={payoutModalOpen}
                onOpenChange={handlePayoutModalChange}
                onClose={handlePayoutRequest}
                setQuote={setRandomQuote}
              />
            </div>
            <RandomQuoteModal
              open={quoteModalOpen}
              onOpenChange={handleQuoteModalChange}
              quote={randomQuote}
            />
          </>
        ) : (
          <Button variant="outline" disabled className="h-full text-textColor">
            Потратить
          </Button>
        )}
      </div>
    </div>
  );
};
